import type { TechnicalRecommendation } from "@repo/database";
import type { RobotsTxtStatus, RobotsTxtReportData } from "./robots-txt-analyzer";

// Re-export for convenience
export type { RobotsTxtStatus };

/**
 * Robots.txt analysis data for recommendations
 * This is an alias for RobotsTxtReportData for backward compatibility
 */
export type RobotsTxtAnalysisData = RobotsTxtReportData;

export interface RecommendationsSummary {
  brandConsistencyIssues: number;
  lowShareOfVoice: boolean;
  technicalRecommendations: number;
  totalPriorityIssues: number;
  robotsTxtStatus?: RobotsTxtStatus;
  robotsTxtBlockedBots?: number;
}

export interface BrandConsistencyData {
  brandSnapshot?: any;
  llmResults?: Record<string, any>;
  platformScores?: Record<string, number>;
}

export interface DiscoveryStats {
  totalQueries: number;
  brandMentions: number;
  shareOfVoice: number;
  citations: number;
}

/**
 * Calculate a summary of recommendations based on brand consistency, share of voice, technical issues, and robots.txt
 */
export function calculateRecommendationsSummary(
  brandConsistencyData?: BrandConsistencyData,
  discoveryStats?: DiscoveryStats,
  technicalRecommendations: TechnicalRecommendation[] = [],
  robotsTxtAnalysis?: RobotsTxtAnalysisData
): RecommendationsSummary {
  let brandConsistencyIssues = 0;
  let lowShareOfVoice = false;
  let technicalRecommendationsCount = technicalRecommendations.length;

  // Count brand consistency issues (platforms with scores below 80)
  if (brandConsistencyData?.platformScores) {
    brandConsistencyIssues = Object.values(brandConsistencyData.platformScores).filter(
      (score) => score < 80
    ).length;
  }

  // Check for low share of voice (below 30%)
  if (discoveryStats) {
    lowShareOfVoice = discoveryStats.shareOfVoice < 30;
  }

  // Count high priority technical recommendations
  const highPriorityTechnical = technicalRecommendations.filter(
    (rec) => rec.priority === 'high'
  ).length;

  // Count robots.txt blocked bots
  const robotsTxtBlockedBots = robotsTxtAnalysis 
    ? robotsTxtAnalysis.blockedBots.aiVisitors.length + 
      robotsTxtAnalysis.blockedBots.aiModelTrainers.length
    : 0;

  // Robots.txt issues count as priority issues if status is red
  const robotsTxtPriorityIssue = robotsTxtAnalysis?.status === "red" ? 1 : 0;

  // Total priority issues = brand consistency issues + low SOV + high priority technical + robots.txt red
  const totalPriorityIssues = brandConsistencyIssues + 
                              (lowShareOfVoice ? 1 : 0) + 
                              highPriorityTechnical +
                              robotsTxtPriorityIssue;

  return {
    brandConsistencyIssues,
    lowShareOfVoice,
    technicalRecommendations: technicalRecommendationsCount,
    totalPriorityIssues,
    robotsTxtStatus: robotsTxtAnalysis?.status,
    robotsTxtBlockedBots: robotsTxtBlockedBots > 0 ? robotsTxtBlockedBots : undefined,
  };
}

/**
 * Analyze brand consistency issues for a specific platform
 */
export function analyzePlatformConsistency(
  platform: string, 
  score: number, 
  llmResult: any, 
  brandSnapshot: any
) {
  if (!llmResult || !brandSnapshot) return null;

  const issues = [];
  const platformDisplayName = platform === 'openai' ? 'ChatGPT' : 
                             platform === 'claude' ? 'Claude' : 
                             platform === 'gemini' ? 'Google Gemini' : 
                             platform === 'perplexity' ? 'Perplexity' : platform;

  // Check for feature mismatches
  if (llmResult.features && brandSnapshot.features) {
    const llmFeatures = llmResult.features || [];
    const actualFeatures = brandSnapshot.features || [];
    const missingFeatures = actualFeatures.filter((f: string) => 
      !llmFeatures.some((lf: string) => lf.toLowerCase().includes(f.toLowerCase()))
    );
    if (missingFeatures.length > 0) {
      issues.push({
        type: 'missing_features',
        description: `Missing ${missingFeatures.length} key features in AI responses`,
        details: missingFeatures.slice(0, 3) // Show top 3
      });
    }
  }

  // Check for differentiator issues
  if (llmResult.differentiators && brandSnapshot.differentiators) {
    const llmDiffs = llmResult.differentiators || [];
    const actualDiffs = brandSnapshot.differentiators || [];
    if (actualDiffs.length > 0 && llmDiffs.length === 0) {
      issues.push({
        type: 'missing_differentiators',
        description: 'Key differentiators not recognized by AI',
        details: actualDiffs.slice(0, 2)
      });
    }
  }

  // Check for category/positioning issues
  if (llmResult.what && brandSnapshot.category) {
    const llmCategory = llmResult.what.toLowerCase();
    const actualCategory = brandSnapshot.category.toLowerCase();
    if (!llmCategory.includes(actualCategory) && !actualCategory.includes(llmCategory)) {
      issues.push({
        type: 'category_mismatch',
        description: 'AI describes your company differently than your website',
        details: [`AI says: "${llmResult.what}"`, `Website says: "${brandSnapshot.category}"`]
      });
    }
  }

  return {
    platform: platformDisplayName,
    score,
    issues,
    isUnknown: score < 20 // Brand is essentially unknown to this AI
  };
}

/**
 * Get platform-specific guide URLs
 */
export function getPlatformGuideUrl(platform: string): string {
  const platformMap: Record<string, string> = {
    'openai': '/docs/ai-visibility-guides/chatgpt',
    'claude': '/docs/ai-visibility-guides/claude',
    'gemini': '/docs/ai-visibility-guides/google-gemini',
    'perplexity': '/docs/ai-visibility-guides/perplexity'
  };
  return platformMap[platform] || '/docs/ai-visibility-guides/overview';
}

/**
 * English fallback labels for technical-recommendation `titleKey` values.
 *
 * The on-page report resolves these via next-intl's `useTranslations`. The
 * PDF/PPT export runs server-side without a translator hook, so it pulls
 * the English label out of this map. Keep it in sync with
 * `packages/i18n/translations/en.json` (paths under `technical.recommendations.*`
 * and `technical.robotsTxt.*`). When proper i18n is wired into `@repo/core`,
 * this map should be retired in favor of `getMessagesForLocale("en")`.
 */
export const TECHNICAL_TITLE_KEY_LABELS: Record<string, string> = {
  // Standard technical recommendations (seeded by background-jobs/utils/technical-analysis.ts)
  "technical.recommendations.json_ld.title": "Implement Structured Data Markup",
  "technical.recommendations.accessibility.title": "Improve Accessibility Compliance",
  "technical.recommendations.readability.title": "Optimize Content Readability",
  "technical.recommendations.performance.title": "Enhance Page Performance",
  // Robots.txt recommendations (produced by analyzeRobotsTxtStatus below)
  "technical.robotsTxt.wildcardDisallow.title": "Robots.txt Blocks All Crawlers",
  "technical.robotsTxt.blockingAiVisitors.title": "Blocking AI Visitors",
  "technical.robotsTxt.blockingAiTrainers.title": "Blocking AI Model Trainers",
};

/**
 * Resolve a technical-recommendation `titleKey` to its English label. Falls
 * back to humanizing the trailing path segment if the key isn't in the map
 * (e.g. `technical.recommendations.foo_bar.title` → "Foo Bar") so unknown
 * keys never leak into the PDF as raw paths.
 */
export function resolveTechnicalTitleLabel(titleKey: string): string {
  const known = TECHNICAL_TITLE_KEY_LABELS[titleKey];
  if (known) return known;
  if (!titleKey) return "";
  const trailing = titleKey.split(".").slice(-2, -1)[0] || titleKey;
  return trailing
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/**
 * Robots.txt recommendation result with i18n keys
 *
 * All text fields are i18n translation keys under "technical.robotsTxt.*"
 * The consumer should use these keys with their i18n library (e.g., next-intl)
 */
export interface RobotsTxtRecommendation {
  severity: 'critical' | 'warning' | 'ok';
  /** i18n key for title (e.g., "technical.robotsTxt.wildcardDisallow.title") */
  titleKey: string;
  /** i18n key for description (e.g., "technical.robotsTxt.wildcardDisallow.description") */
  descriptionKey: string;
  /** i18n key for impact (e.g., "technical.robotsTxt.wildcardDisallow.impact") */
  impactKey: string;
  /** i18n key for actions array (e.g., "technical.robotsTxt.wildcardDisallow.actions") */
  actionsKey: string;
  /** Variables to interpolate into description (e.g., { blockedBots: "GPTBot, ClaudeBot" }) */
  interpolationVars?: Record<string, string | number>;
  /** Blocked bots data for display */
  blockedBots: {
    aiVisitors: Array<{ name: string; company?: string }>;
    aiModelTrainers: Array<{ name: string; company?: string }>;
  };
  /** URL to documentation */
  docsUrl: string;
}

/**
 * Analyze robots.txt status and generate recommendations with i18n keys
 * 
 * Returns translation keys instead of hardcoded strings. The consumer
 * should use these keys with their i18n library.
 * 
 * @example
 * ```tsx
 * const result = analyzeRobotsTxtStatus(analysis);
 * if (result) {
 *   const title = t(result.titleKey);
 *   const description = t(result.descriptionKey, result.interpolationVars);
 *   const actions = t.raw(result.actionsKey) as string[];
 * }
 * ```
 */
export function analyzeRobotsTxtStatus(analysis: RobotsTxtAnalysisData): RobotsTxtRecommendation | null {
  if (analysis.status === "green") {
    return null; // No issues to report
  }

  const { blockedBots } = analysis;
  const docsUrl = "/docs/help/is-robots-txt-blocking-ai-on-your-site";

  if (analysis.status === "red") {
    // Critical: AI visitors or wildcard disallow
    if (analysis.hasWildcardDisallow) {
      return {
        severity: 'critical',
        titleKey: 'technical.robotsTxt.wildcardDisallow.title',
        descriptionKey: 'technical.robotsTxt.wildcardDisallow.description',
        impactKey: 'technical.robotsTxt.wildcardDisallow.impact',
        actionsKey: 'technical.robotsTxt.wildcardDisallow.actions',
        blockedBots,
        docsUrl,
      };
    }

    if (blockedBots.aiVisitors.length > 0) {
      const botNames = blockedBots.aiVisitors.slice(0, 3).map(b => b.name).join(', ');
      const moreCount = blockedBots.aiVisitors.length > 3 ? blockedBots.aiVisitors.length - 3 : 0;
      const blockedBotsDisplay = moreCount > 0 ? `${botNames} and ${moreCount} more` : botNames;
      
      return {
        severity: 'critical',
        titleKey: 'technical.robotsTxt.blockingAiVisitors.title',
        descriptionKey: 'technical.robotsTxt.blockingAiVisitors.description',
        impactKey: 'technical.robotsTxt.blockingAiVisitors.impact',
        actionsKey: 'technical.robotsTxt.blockingAiVisitors.actions',
        interpolationVars: { blockedBots: blockedBotsDisplay },
        blockedBots,
        docsUrl,
      };
    }
  }

  if (analysis.status === "yellow" && blockedBots.aiModelTrainers.length > 0) {
    const botNames = blockedBots.aiModelTrainers.slice(0, 3).map(b => b.name).join(', ');
    const moreCount = blockedBots.aiModelTrainers.length > 3 ? blockedBots.aiModelTrainers.length - 3 : 0;
    const blockedBotsDisplay = moreCount > 0 ? `${botNames} and ${moreCount} more` : botNames;
    
    return {
      severity: 'warning',
      titleKey: 'technical.robotsTxt.blockingAiTrainers.title',
      descriptionKey: 'technical.robotsTxt.blockingAiTrainers.description',
      impactKey: 'technical.robotsTxt.blockingAiTrainers.impact',
      actionsKey: 'technical.robotsTxt.blockingAiTrainers.actions',
      interpolationVars: { blockedBots: blockedBotsDisplay },
      blockedBots,
      docsUrl,
    };
  }

  return null;
}

/**
 * Get the i18n key for robots.txt status display
 */
export function getRobotsTxtStatusKey(status: RobotsTxtStatus): string {
  return `technical.robotsTxt.status.${status}`;
}

